<template>
  <main>
    <form class="container" @submit.prevent="submit">
      <al-notify v-if="errorToken" error name="error-msg">
        {{ $t(errorToken) }}
      </al-notify>
      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t('addShipping.heading') }}
          </h1>
        </div>
      </div>

      <tux-google-consent
        v-if="isAutocompleteApiEnabled"
        :privacy-url="googlePrivacyUrl"
        :terms-url="googleTermsUrl" />

      <tux-address-form
        v-model="address"
        :countries="countries"
        :enable-autocomplete="isAutocompleteApiEnabled"
        autocomplete="shipping"
        name="shipping-address"
        @autocompleteSelection="autocompleteSelected = true" />
      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <button
            :disabled="hasValidationErrors"
            class="uppercase fill"
            data-test="form-submit-button"
            type="submit"
            :aria-label="$t('button.continue')">
            {{ $t('button.continue') }}
          </button>
        </div>
        <div class="col-full align-center">
          <back-button v-analytics:click="$EVENTS.GO_BACK_BUTTON" :location="backButtonRoute">
            {{ $t('button.goBack') }}
          </back-button>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { AlNotify } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { TuxAddressForm, TuxGoogleConsent } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import useConfirmModal from '@/composables/useConfirmModal'
import useFormValidation from '@/composables/useFormValidation'
import { COUNTRY_FEATURES, LOADERS, ROUTES, TEST_OVERRIDES } from '@/constants'

export default {
  name: ROUTES.ADD_SHIPPING,
  components: { AlNotify, BackButton, TuxGoogleConsent, TuxAddressForm },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeaveConfirmModal(to, from, next)
  },
  setup(props, context) {
    const { beforeRouteLeaveConfirmModal } = useConfirmModal({ context })
    const { hasValidationErrors } = useFormValidation()

    return { beforeRouteLeaveConfirmModal, hasValidationErrors }
  },
  data() {
    return {
      autocompleteSelected: false,
      address: {
        countryCode: this.$store.state.user.consumer.countryCode
      },
      backButtonRoute: { name: ROUTES.LIST_SHIPPING },
      errorToken: null,
      googlePrivacyUrl: process.env.GOOGLE_PRIVACY,
      googleTermsUrl: process.env.GOOGLE_TERMS
    }
  },
  computed: {
    ...mapState({
      serviceLayerError: (state) => state.serviceLayerError
    }),
    ...mapGetters(['countries', 'locale']),
    isAutocompleteApiEnabled() {
      return (
        this.$switchboard.isCountryFeatureEnabled(COUNTRY_FEATURES.ENABLE_ADDRESS_AUTOCOMPLETE) &&
        !this.$switchboard.isTestOverrideEnabled(TEST_OVERRIDES.GOOGLE_AUTOCOMPLETE)
      )
    }
  },
  created() {
    if (this.$i18nData.isCountryRestricted(this.locale)) {
      this.$router.replace({ name: ROUTES.PROFILE_HOME })
    }
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$validator.validateAll()
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions([
      'clearServiceLayerError',
      'addShippingAddress',
      'startLoader',
      'stopLoader',
      'stopLoaderWithSuccess'
    ]),
    async submit() {
      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: { autocompleteSelected: this.autocompleteSelected }
      })

      this.clearServiceLayerError()
      this.startLoader(LOADERS.UPDATE_PROFILE)

      const addressBody = {
        ...this.address,
        name: `${this.address.firstName} ${this.address.lastName}`
      }
      await this.addShippingAddress(addressBody)
      nudetect.clearBehaviouralData()

      if (this.serviceLayerError) {
        nudetect.beginBehaviouralMonitoring()
        this.errorToken = 'addShipping.enrollmentError'
      } else {
        this.route()
      }

      if (this.serviceLayerError) {
        this.stopLoader(LOADERS.UPDATE_PROFILE)
      } else {
        this.stopLoaderWithSuccess(LOADERS.UPDATE_PROFILE)
      }
    },
    route() {
      // replace the state so the forward button on the browser doesnt bring them back to add
      this.$router.replace({ name: ROUTES.LIST_SHIPPING, params: { skipConfirmation: true } })
    }
  }
}
</script>
